import { lazy, Suspense, useEffect, useState } from 'react';
import { ToastProvider } from './components/Toast';
import { ErrorBoundary } from './components/ErrorBoundary';
import {
  Routes,
  Route,
  Navigate,
  NavLink,
  useNavigate,
  useLocation,
} from 'react-router-dom';
import type { RuntimeConfig } from './config';
import { getUser, handleCallback, login, logout } from './auth';
import { getMe, type MeResponse } from './api';

// ---------------------------------------------------------------------------
// Lazy-loaded pages — each chunk is only fetched when the route is visited.
// All page modules use named exports, so we re-map to { default }.
// ---------------------------------------------------------------------------
const LearnersListPage = lazy(() => import('./pages/LearnersList').then(m => ({ default: m.LearnersListPage })));
const LearnerDetailPage = lazy(() => import('./pages/LearnerDetail').then(m => ({ default: m.LearnerDetailPage })));
const LearnerNewPage = lazy(() => import('./pages/LearnerNew').then(m => ({ default: m.LearnerNewPage })));
const LearnerEditPage = lazy(() => import('./pages/LearnerEdit').then(m => ({ default: m.LearnerEditPage })));
const LearnerImportPage = lazy(() => import('./pages/LearnerImport').then(m => ({ default: m.LearnerImportPage })));
const CoursesPage = lazy(() => import('./pages/CoursesPage').then(m => ({ default: m.CoursesPage })));
const TermsPage = lazy(() => import('./pages/TermsPage').then(m => ({ default: m.TermsPage })));
const RoomsPage = lazy(() => import('./pages/RoomsPage').then(m => ({ default: m.RoomsPage })));
const CohortsListPage = lazy(() => import('./pages/CohortsListPage').then(m => ({ default: m.CohortsListPage })));
const CohortDetailPage = lazy(() => import('./pages/CohortDetailPage').then(m => ({ default: m.CohortDetailPage })));
const GroupsListPage = lazy(() => import('./pages/GroupsListPage').then(m => ({ default: m.GroupsListPage })));
const StaffListPage = lazy(() => import('./pages/StaffListPage').then(m => ({ default: m.StaffListPage })));
const AttendancePage = lazy(() => import('./pages/AttendancePage').then(m => ({ default: m.AttendancePage })));
const TimetablePage = lazy(() => import('./pages/TimetablePage').then(m => ({ default: m.TimetablePage })));
const PipelinePage = lazy(() => import('./pages/PipelinePage').then(m => ({ default: m.PipelinePage })));
const ActivitiesPage = lazy(() => import('./pages/ActivitiesPage').then(m => ({ default: m.ActivitiesPage })));
const InvoicesPage = lazy(() => import('./pages/InvoicesPage').then(m => ({ default: m.InvoicesPage })));
const DashboardPage = lazy(() => import('./pages/DashboardPage').then(m => ({ default: m.DashboardPage })));
const GroupDetailPage = lazy(() => import('./pages/GroupDetailPage').then(m => ({ default: m.GroupDetailPage })));
const InvoiceDetailPage = lazy(() => import('./pages/InvoiceDetailPage').then(m => ({ default: m.InvoiceDetailPage })));
const DealDetailPage = lazy(() => import('./pages/DealDetailPage').then(m => ({ default: m.DealDetailPage })));
const StaffDetailPage = lazy(() => import('./pages/StaffDetailPage').then(m => ({ default: m.StaffDetailPage })));
const PipelineSettingsPage = lazy(() => import('./pages/PipelineSettingsPage').then(m => ({ default: m.PipelineSettingsPage })));
const SendPage = lazy(() => import('./pages/SendPage').then(m => ({ default: m.SendPage })));
const SafeguardingPage = lazy(() => import('./pages/SafeguardingPage').then(m => ({ default: m.SafeguardingPage })));
const DocumentsPage = lazy(() => import('./pages/DocumentsPage').then(m => ({ default: m.DocumentsPage })));
const AssessmentsPage = lazy(() => import('./pages/AssessmentsPage').then(m => ({ default: m.AssessmentsPage })));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage').then(m => ({ default: m.NotificationsPage })));
const OrgSettingsPage = lazy(() => import('./pages/OrgSettingsPage').then(m => ({ default: m.OrgSettingsPage })));
const AuditLogPage = lazy(() => import('./pages/AuditLogPage').then(m => ({ default: m.AuditLogPage })));
const DataExportPage = lazy(() => import('./pages/DataExportPage').then(m => ({ default: m.DataExportPage })));
const BulkOperationsPage = lazy(() => import('./pages/BulkOperationsPage').then(m => ({ default: m.BulkOperationsPage })));
const ExamsPage = lazy(() => import('./pages/ExamsPage').then(m => ({ default: m.ExamsPage })));
const ProgressPage = lazy(() => import('./pages/ProgressPage').then(m => ({ default: m.ProgressPage })));
const FundingPage = lazy(() => import('./pages/FundingPage').then(m => ({ default: m.FundingPage })));
const EmployersPage = lazy(() => import('./pages/EmployersPage').then(m => ({ default: m.EmployersPage })));
const SitesPage = lazy(() => import('./pages/SitesPage').then(m => ({ default: m.SitesPage })));
const CalendarPage = lazy(() => import('./pages/CalendarPage').then(m => ({ default: m.CalendarPage })));
const RoomBookingPage = lazy(() => import('./pages/RoomBookingPage').then(m => ({ default: m.RoomBookingPage })));
const UserManagementPage = lazy(() => import('./pages/UserManagementPage').then(m => ({ default: m.UserManagementPage })));
const SearchPage = lazy(() => import('./pages/SearchPage').then(m => ({ default: m.SearchPage })));
const YearRolloverPage = lazy(() => import('./pages/YearRolloverPage').then(m => ({ default: m.YearRolloverPage })));
const EmailIntegrationPage = lazy(() => import('./pages/EmailIntegrationPage').then(m => ({ default: m.EmailIntegrationPage })));
const SmsIntegrationPage = lazy(() => import('./pages/SmsIntegrationPage').then(m => ({ default: m.SmsIntegrationPage })));
const ParentPortalPage = lazy(() => import('./pages/ParentPortalPage').then(m => ({ default: m.ParentPortalPage })));
const LearnerPortalPage = lazy(() => import('./pages/LearnerPortalPage').then(m => ({ default: m.LearnerPortalPage })));
const ReportsDashboardPage = lazy(() => import('./pages/ReportsDashboardPage').then(m => ({ default: m.ReportsDashboardPage })));
const AttendanceReportPage = lazy(() => import('./pages/AttendanceReportPage').then(m => ({ default: m.AttendanceReportPage })));
const AchievementReportPage = lazy(() => import('./pages/AchievementReportPage').then(m => ({ default: m.AchievementReportPage })));
const KpiDashboardPage = lazy(() => import('./pages/KpiDashboardPage').then(m => ({ default: m.KpiDashboardPage })));
const FinancialReportPage = lazy(() => import('./pages/FinancialReportPage').then(m => ({ default: m.FinancialReportPage })));
const DbsChecksPage = lazy(() => import('./pages/DbsChecksPage').then(m => ({ default: m.DbsChecksPage })));
const IlrExportPage = lazy(() => import('./pages/IlrExportPage').then(m => ({ default: m.IlrExportPage })));
const CensusReturnPage = lazy(() => import('./pages/CensusReturnPage').then(m => ({ default: m.CensusReturnPage })));
const CustomReportPage = lazy(() => import('./pages/CustomReportPage').then(m => ({ default: m.CustomReportPage })));
const PortalManagementPage = lazy(() => import('./pages/PortalManagementPage').then(m => ({ default: m.PortalManagementPage })));
const PaymentsPage = lazy(() => import('./pages/PaymentsPage').then(m => ({ default: m.PaymentsPage })));
const ApprenticeshipPage = lazy(() => import('./pages/ApprenticeshipPage').then(m => ({ default: m.ApprenticeshipPage })));
const QualificationsPage = lazy(() => import('./pages/QualificationsPage').then(m => ({ default: m.QualificationsPage })));
const PredictiveAnalyticsPage = lazy(() => import('./pages/PredictiveAnalyticsPage').then(m => ({ default: m.PredictiveAnalyticsPage })));
const MultiYearPage = lazy(() => import('./pages/MultiYearPage').then(m => ({ default: m.MultiYearPage })));
const ApiKeysPage = lazy(() => import('./pages/ApiKeysPage').then(m => ({ default: m.ApiKeysPage })));
const SystemHealthPage = lazy(() => import('./pages/SystemHealthPage').then(m => ({ default: m.SystemHealthPage })));
const BackupConfigPage = lazy(() => import('./pages/BackupConfigPage').then(m => ({ default: m.BackupConfigPage })));
const RateLimitPage = lazy(() => import('./pages/RateLimitPage').then(m => ({ default: m.RateLimitPage })));
const HelpIndexPage = lazy(() => import('./pages/HelpIndexPage').then(m => ({ default: m.HelpIndexPage })));
const HelpArticlePage = lazy(() => import('./pages/HelpArticlePage').then(m => ({ default: m.HelpArticlePage })));

interface AuthState {
  status: 'loading' | 'anonymous' | 'authenticated';
  me: MeResponse | null;
  error: string | null;
}

export function App({ config }: { config: RuntimeConfig }): JSX.Element {
  const [auth, setAuth] = useState<AuthState>({
    status: 'loading',
    me: null,
    error: null,
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const user = await getUser();
        if (!user || user.expired) {
          if (!cancelled) setAuth({ status: 'anonymous', me: null, error: null });
          return;
        }
        // JIT-provision on the API side by calling /me — this is the
        // first endpoint every authenticated session hits, and the
        // TenantResolverInterceptor will upsert the local app_user row
        // + attach orgId here.
        const me = await getMe(config);
        if (!cancelled)
          setAuth({ status: 'authenticated', me, error: null });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[atlas-web] auth bootstrap error:', err);
        if (!cancelled)
          setAuth({
            status: 'anonymous',
            me: null,
            error: String(err instanceof Error ? err.message : err),
          });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [config]);

  return (
    <Routes>
      <Route path="/callback" element={<CallbackPage />} />
      <Route
        path="/*"
        element={<AuthedShell config={config} auth={auth} />}
      />
    </Routes>
  );
}

function CallbackPage(): JSX.Element {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        await handleCallback();
        // Pop the ?code=... and ?state=... off the URL and go to the
        // learners list. Replace so the back button doesn't land on /callback.
        navigate('/dashboard', { replace: true });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[atlas-web] callback error:', err);
        setError(String(err instanceof Error ? err.message : err));
      }
    })();
  }, [navigate]);

  return (
    <SignedOutLayout>
      {error ? (
        <div className="error">
          Login failed: {error}
          <div style={{ marginTop: '1rem' }}>
            <button onClick={() => void login()}>Try again</button>
          </div>
        </div>
      ) : (
        <p className="muted">Completing sign-in…</p>
      )}
    </SignedOutLayout>
  );
}

/**
 * Bare-bones layout for unauthenticated states (loading, sign-in, callback).
 * Centred card on the parchment canvas — no sidebar yet because we don't
 * know the tenant.
 */
function SignedOutLayout({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        padding: '2rem 1rem',
      }}
    >
      <div style={{ width: '100%', maxWidth: 460 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.85rem',
            marginBottom: '1.5rem',
          }}
        >
          <img src="/rise-logo.svg" alt="Rise — Resolve. Inspire. Support. Empower." className="atlas-brand-logo-full" style={{ height: '48px', borderRadius: '6px' }} />
          <div className="atlas-brand-text" style={{ display: 'none' }}>
            <span className="atlas-brand-name" style={{ color: 'var(--paper-800)' }}>
              Project Atlas
            </span>
            <span className="atlas-brand-tenant" style={{ color: 'var(--text-muted)' }}>
              MIS · CRM · Programmes
            </span>
          </div>
        </div>
        <div className="card">{children}</div>
      </div>
    </div>
  );
}

function AuthedShell({
  config,
  auth,
}: {
  config: RuntimeConfig;
  auth: AuthState;
}): JSX.Element {
  if (auth.status === 'loading') {
    return (
      <SignedOutLayout>
        <p className="muted">Loading your workspace…</p>
      </SignedOutLayout>
    );
  }

  if (auth.status === 'anonymous') {
    return (
      <SignedOutLayout>
        <h2 style={{ marginTop: 0 }}>Welcome back</h2>
        <p className="muted" style={{ marginTop: 0 }}>
          Sign in with your organisation Keycloak account to continue.
        </p>
        {auth.error && <div className="error">{auth.error}</div>}
        <div style={{ marginTop: '1.25rem' }}>
          <button className="primary" onClick={() => void login()}>
            Sign in with Keycloak
          </button>
        </div>
      </SignedOutLayout>
    );
  }

  // authenticated
  const me = auth.me!;
  return (
    <ToastProvider>
    <a href="#main-content" className="skip-link">Skip to main content</a>
    <div className="atlas-shell">
      <Sidebar me={me} />
      <div className="atlas-main">
        <Topbar me={me} />
        <main className="atlas-content" role="main" id="main-content">
          <ErrorBoundary>
          <Suspense fallback={<div className="card"><p className="muted">Loading…</p></div>}>
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route
              path="/dashboard"
              element={<DashboardPage config={config} />}
            />
            <Route
              path="/learners"
              element={<LearnersListPage config={config} />}
            />
            <Route
              path="/learners/new"
              element={<LearnerNewPage config={config} />}
            />
            <Route
              path="/learners/import"
              element={<LearnerImportPage config={config} />}
            />
            <Route
              path="/learners/:id/edit"
              element={<LearnerEditPage config={config} />}
            />
            <Route
              path="/learners/:id"
              element={<LearnerDetailPage config={config} />}
            />
            <Route
              path="/programmes"
              element={<Navigate to="/programmes/cohorts" replace />}
            />
            <Route
              path="/programmes/courses"
              element={<CoursesPage config={config} />}
            />
            <Route
              path="/programmes/terms"
              element={<TermsPage config={config} />}
            />
            <Route
              path="/programmes/rooms"
              element={<RoomsPage config={config} />}
            />
            <Route
              path="/programmes/cohorts"
              element={<CohortsListPage config={config} />}
            />
            <Route
              path="/programmes/cohorts/:id"
              element={<CohortDetailPage config={config} />}
            />
            <Route
              path="/programmes/groups"
              element={<GroupsListPage config={config} />}
            />
            <Route
              path="/programmes/groups/:id"
              element={<GroupDetailPage config={config} />}
            />
            <Route
              path="/mis/attendance"
              element={<AttendancePage config={config} />}
            />
            <Route
              path="/mis/timetable"
              element={<TimetablePage config={config} />}
            />
            <Route
              path="/mis/send"
              element={<SendPage config={config} />}
            />
            <Route
              path="/mis/safeguarding"
              element={<SafeguardingPage config={config} />}
            />
            <Route
              path="/mis/documents"
              element={<DocumentsPage config={config} />}
            />
            <Route
              path="/mis/assessments"
              element={<AssessmentsPage config={config} />}
            />
            <Route
              path="/crm/pipeline"
              element={<PipelinePage config={config} />}
            />
            <Route
              path="/crm/pipeline/:id/settings"
              element={<PipelineSettingsPage config={config} />}
            />
            <Route
              path="/crm/deals/:id"
              element={<DealDetailPage config={config} />}
            />
            <Route
              path="/crm/activities"
              element={<ActivitiesPage config={config} />}
            />
            <Route
              path="/finance/invoices"
              element={<InvoicesPage config={config} />}
            />
            <Route
              path="/finance/invoices/:id"
              element={<InvoiceDetailPage config={config} />}
            />
            <Route
              path="/people/staff"
              element={<StaffListPage config={config} />}
            />
            <Route
              path="/people/staff/:id"
              element={<StaffDetailPage config={config} />}
            />
            <Route
              path="/notifications"
              element={<NotificationsPage config={config} />}
            />
            <Route
              path="/settings"
              element={<OrgSettingsPage config={config} />}
            />
            <Route
              path="/system/audit-log"
              element={<AuditLogPage config={config} />}
            />
            <Route
              path="/system/export"
              element={<DataExportPage config={config} />}
            />
            <Route
              path="/system/bulk"
              element={<BulkOperationsPage config={config} />}
            />
            <Route
              path="/mis/exams"
              element={<ExamsPage config={config} />}
            />
            <Route
              path="/mis/progress"
              element={<ProgressPage config={config} />}
            />
            <Route
              path="/mis/calendar"
              element={<CalendarPage config={config} />}
            />
            <Route
              path="/finance/funding"
              element={<FundingPage config={config} />}
            />
            <Route
              path="/people/employers"
              element={<EmployersPage config={config} />}
            />
            <Route
              path="/programmes/room-booking"
              element={<RoomBookingPage config={config} />}
            />
            <Route
              path="/system/sites"
              element={<SitesPage config={config} />}
            />
            <Route
              path="/system/users"
              element={<UserManagementPage config={config} />}
            />
            <Route
              path="/system/search"
              element={<SearchPage config={config} />}
            />
            <Route
              path="/system/year-rollover"
              element={<YearRolloverPage config={config} />}
            />
            <Route
              path="/system/email"
              element={<EmailIntegrationPage config={config} />}
            />
            <Route
              path="/system/sms"
              element={<SmsIntegrationPage config={config} />}
            />
            <Route
              path="/system/parent-portal"
              element={<ParentPortalPage config={config} />}
            />
            <Route
              path="/system/learner-portal"
              element={<LearnerPortalPage config={config} />}
            />
            <Route
              path="/reports"
              element={<ReportsDashboardPage config={config} />}
            />
            <Route
              path="/reports/attendance"
              element={<AttendanceReportPage config={config} />}
            />
            <Route
              path="/reports/achievement"
              element={<AchievementReportPage config={config} />}
            />
            <Route
              path="/reports/kpi"
              element={<KpiDashboardPage config={config} />}
            />
            <Route
              path="/reports/financial"
              element={<FinancialReportPage config={config} />}
            />
            <Route
              path="/reports/dbs"
              element={<DbsChecksPage config={config} />}
            />
            <Route
              path="/reports/ilr"
              element={<IlrExportPage config={config} />}
            />
            <Route
              path="/reports/census"
              element={<CensusReturnPage config={config} />}
            />
            <Route
              path="/reports/custom"
              element={<CustomReportPage config={config} />}
            />
            <Route
              path="/system/portal"
              element={<PortalManagementPage config={config} />}
            />
            <Route
              path="/finance/payments"
              element={<PaymentsPage config={config} />}
            />
            <Route
              path="/mis/apprenticeships"
              element={<ApprenticeshipPage config={config} />}
            />
            <Route
              path="/mis/qualifications"
              element={<QualificationsPage config={config} />}
            />
            <Route
              path="/mis/analytics"
              element={<PredictiveAnalyticsPage config={config} />}
            />
            <Route
              path="/mis/multi-year"
              element={<MultiYearPage config={config} />}
            />
            <Route
              path="/system/api-keys"
              element={<ApiKeysPage config={config} />}
            />
            <Route
              path="/system/health"
              element={<SystemHealthPage config={config} />}
            />
            <Route
              path="/system/backups"
              element={<BackupConfigPage config={config} />}
            />
            <Route
              path="/system/rate-limits"
              element={<RateLimitPage config={config} />}
            />
            <Route path="/help" element={<HelpIndexPage />} />
            <Route path="/help/*" element={<HelpArticlePage />} />
            <Route
              path="*"
              element={
                <div className="card">
                  <h2 style={{ marginTop: 0 }}>Not found</h2>
                  <p className="muted">Nothing here yet.</p>
                </div>
              }
            />
          </Routes>
          </Suspense>
          </ErrorBoundary>
        </main>
      </div>
    </div>
    </ToastProvider>
  );
}

// ---------------------------------------------------------------------------
// Sidebar
// ---------------------------------------------------------------------------

function Sidebar({ me }: { me: MeResponse }): JSX.Element {
  return (
    <aside className="atlas-sidebar" role="complementary">
      <div className="atlas-brand">
        <img src="/rise-icon.svg" alt="Rise" className="atlas-brand-mark-img" />
        <div className="atlas-brand-text">
          <span className="atlas-brand-name">Project Atlas</span>
          <span className="atlas-brand-tenant">{me.organisation.name}</span>
        </div>
      </div>

      <nav className="atlas-nav" role="navigation" aria-label="Main navigation">
        <NavLink to="/dashboard" className="atlas-nav-dashboard">
          <span className="atlas-nav-icon" aria-hidden="true">
            {Icon.grid}
          </span>
          Dashboard
        </NavLink>

        <div className="atlas-nav-group">
          <span className="atlas-nav-label">Sales</span>
          <NavLink to="/learners">
            <span className="atlas-nav-icon" aria-hidden="true">
              {Icon.users}
            </span>
            Learners
          </NavLink>
          <NavLink to="/learners/import">
            <span className="atlas-nav-icon" aria-hidden="true">
              {Icon.upload}
            </span>
            Import
          </NavLink>
        </div>

        <div className="atlas-nav-group">
          <span className="atlas-nav-label">Programmes</span>
          <NavLink to="/programmes/cohorts">
            <span className="atlas-nav-icon" aria-hidden="true">
              {Icon.grid}
            </span>
            Cohorts
          </NavLink>
          <NavLink to="/programmes/courses">
            <span className="atlas-nav-icon" aria-hidden="true">
              {Icon.book}
            </span>
            Courses
          </NavLink>
          <NavLink to="/programmes/terms">
            <span className="atlas-nav-icon" aria-hidden="true">
              {Icon.calendar}
            </span>
            Terms
          </NavLink>
          <NavLink to="/programmes/rooms">
            <span className="atlas-nav-icon" aria-hidden="true">
              {Icon.pin}
            </span>
            Rooms
          </NavLink>
          <NavLink to="/programmes/groups">
            <span className="atlas-nav-icon" aria-hidden="true">
              {Icon.grid}
            </span>
            Groups
          </NavLink>
          <NavLink to="/programmes/room-booking">
            <span className="atlas-nav-icon" aria-hidden="true">
              {Icon.pin}
            </span>
            Room Booking
          </NavLink>
        </div>

        <div className="atlas-nav-group">
          <span className="atlas-nav-label">MIS</span>
          <NavLink to="/mis/attendance">
            <span className="atlas-nav-icon" aria-hidden="true">
              {Icon.calendar}
            </span>
            Attendance
          </NavLink>
          <NavLink to="/mis/timetable">
            <span className="atlas-nav-icon" aria-hidden="true">
              {Icon.grid}
            </span>
            Timetable
          </NavLink>
          <NavLink to="/mis/send">
            <span className="atlas-nav-icon" aria-hidden="true">
              {Icon.users}
            </span>
            SEND
          </NavLink>
          <NavLink to="/mis/safeguarding">
            <span className="atlas-nav-icon" aria-hidden="true">
              {Icon.shield}
            </span>
            Safeguarding
          </NavLink>
          <NavLink to="/mis/documents">
            <span className="atlas-nav-icon" aria-hidden="true">
              {Icon.book}
            </span>
            Documents
          </NavLink>
          <NavLink to="/mis/assessments">
            <span className="atlas-nav-icon" aria-hidden="true">
              {Icon.calendar}
            </span>
            Assessments
          </NavLink>
          <NavLink to="/mis/exams">
            <span className="atlas-nav-icon" aria-hidden="true">
              {Icon.book}
            </span>
            Exams
          </NavLink>
          <NavLink to="/mis/progress">
            <span className="atlas-nav-icon" aria-hidden="true">
              {Icon.grid}
            </span>
            Progress
          </NavLink>
          <NavLink to="/mis/calendar">
            <span className="atlas-nav-icon" aria-hidden="true">
              {Icon.calendar}
            </span>
            Calendar
          </NavLink>
          <NavLink to="/mis/apprenticeships">
            <span className="atlas-nav-icon" aria-hidden="true">
              {Icon.book}
            </span>
            Apprenticeships
          </NavLink>
          <NavLink to="/mis/qualifications">
            <span className="atlas-nav-icon" aria-hidden="true">
              {Icon.book}
            </span>
            Qualifications
          </NavLink>
          <NavLink to="/mis/analytics">
            <span className="atlas-nav-icon" aria-hidden="true">
              {Icon.grid}
            </span>
            Analytics
          </NavLink>
          <NavLink to="/mis/multi-year">
            <span className="atlas-nav-icon" aria-hidden="true">
              {Icon.calendar}
            </span>
            Multi-Year
          </NavLink>
        </div>

        <div className="atlas-nav-group">
          <span className="atlas-nav-label">CRM</span>
          <NavLink to="/crm/pipeline">
            <span className="atlas-nav-icon" aria-hidden="true">
              {Icon.grid}
            </span>
            Pipeline
          </NavLink>
          <NavLink to="/crm/activities">
            <span className="atlas-nav-icon" aria-hidden="true">
              {Icon.calendar}
            </span>
            Activities
          </NavLink>
        </div>

        <div className="atlas-nav-group">
          <span className="atlas-nav-label">Finance</span>
          <NavLink to="/finance/invoices">
            <span className="atlas-nav-icon" aria-hidden="true">
              {Icon.book}
            </span>
            Invoices
          </NavLink>
          <NavLink to="/finance/funding">
            <span className="atlas-nav-icon" aria-hidden="true">
              {Icon.book}
            </span>
            Funding
          </NavLink>
          <NavLink to="/finance/payments">
            <span className="atlas-nav-icon" aria-hidden="true">
              {Icon.book}
            </span>
            Payments
          </NavLink>
        </div>

        <div className="atlas-nav-group">
          <span className="atlas-nav-label">People</span>
          <NavLink to="/people/staff">
            <span className="atlas-nav-icon" aria-hidden="true">
              {Icon.users}
            </span>
            Staff
          </NavLink>
          <NavLink to="/people/employers">
            <span className="atlas-nav-icon" aria-hidden="true">
              {Icon.users}
            </span>
            Employers
          </NavLink>
        </div>

        <div className="atlas-nav-group">
          <span className="atlas-nav-label">Reports</span>
          <NavLink to="/reports">
            <span className="atlas-nav-icon" aria-hidden="true">
              {Icon.grid}
            </span>
            Dashboard
          </NavLink>
          <NavLink to="/reports/dbs">
            <span className="atlas-nav-icon" aria-hidden="true">
              {Icon.shield}
            </span>
            DBS Checks
          </NavLink>
        </div>

        <div className="atlas-nav-group">
          <span className="atlas-nav-label">System</span>
          <NavLink to="/notifications">
            <span className="atlas-nav-icon" aria-hidden="true">
              {Icon.bell}
            </span>
            Notifications
          </NavLink>
          <NavLink to="/settings">
            <span className="atlas-nav-icon" aria-hidden="true">
              {Icon.grid}
            </span>
            Settings
          </NavLink>
          <NavLink to="/system/audit-log">
            <span className="atlas-nav-icon" aria-hidden="true">
              {Icon.book}
            </span>
            Audit Log
          </NavLink>
          <NavLink to="/system/export">
            <span className="atlas-nav-icon" aria-hidden="true">
              {Icon.upload}
            </span>
            Data Export
          </NavLink>
          <NavLink to="/system/bulk">
            <span className="atlas-nav-icon" aria-hidden="true">
              {Icon.grid}
            </span>
            Bulk Ops
          </NavLink>
          <NavLink to="/system/sites">
            <span className="atlas-nav-icon" aria-hidden="true">
              {Icon.pin}
            </span>
            Sites
          </NavLink>
          <NavLink to="/system/users">
            <span className="atlas-nav-icon" aria-hidden="true">
              {Icon.users}
            </span>
            Users
          </NavLink>
          <NavLink to="/system/search">
            <span className="atlas-nav-icon" aria-hidden="true">
              {Icon.grid}
            </span>
            Search
          </NavLink>
          <NavLink to="/system/year-rollover">
            <span className="atlas-nav-icon" aria-hidden="true">
              {Icon.calendar}
            </span>
            Year Rollover
          </NavLink>
          <NavLink to="/system/email">
            <span className="atlas-nav-icon" aria-hidden="true">
              {Icon.bell}
            </span>
            Email
          </NavLink>
          <NavLink to="/system/sms">
            <span className="atlas-nav-icon" aria-hidden="true">
              {Icon.bell}
            </span>
            SMS
          </NavLink>
          <NavLink to="/system/parent-portal">
            <span className="atlas-nav-icon" aria-hidden="true">
              {Icon.users}
            </span>
            Parent Portal
          </NavLink>
          <NavLink to="/system/learner-portal">
            <span className="atlas-nav-icon" aria-hidden="true">
              {Icon.users}
            </span>
            Learner Portal
          </NavLink>
          <NavLink to="/system/portal">
            <span className="atlas-nav-icon" aria-hidden="true">
              {Icon.users}
            </span>
            Portal Tokens
          </NavLink>
          <NavLink to="/system/api-keys">
            <span className="atlas-nav-icon" aria-hidden="true">
              {Icon.shield}
            </span>
            API Keys
          </NavLink>
          <NavLink to="/system/health">
            <span className="atlas-nav-icon" aria-hidden="true">
              {Icon.grid}
            </span>
            System Health
          </NavLink>
          <NavLink to="/system/backups">
            <span className="atlas-nav-icon" aria-hidden="true">
              {Icon.upload}
            </span>
            Backups
          </NavLink>
          <NavLink to="/system/rate-limits">
            <span className="atlas-nav-icon" aria-hidden="true">
              {Icon.shield}
            </span>
            Rate Limits
          </NavLink>
        </div>

        <div className="atlas-nav-group">
          <span className="atlas-nav-label">Help</span>
          <NavLink to="/help">
            <span className="atlas-nav-icon" aria-hidden="true">
              {Icon.book}
            </span>
            Knowledge Base
          </NavLink>
        </div>
      </nav>

      <div className="atlas-user">
        <div className="atlas-user-avatar">{initials(me.name)}</div>
        <div className="atlas-user-meta">
          <div className="atlas-user-name">{me.name}</div>
          <div className="atlas-user-email">{me.email}</div>
        </div>
        <button
          type="button"
          className="atlas-user-action"
          onClick={() => void logout()}
        >
          Sign out
        </button>
      </div>
    </aside>
  );
}

function Topbar({ me }: { me: MeResponse }): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchQ, setSearchQ] = useState('');
  const sectionLabel = topbarLabel(location.pathname);
  return (
    <div className="atlas-topbar" role="banner">
      <div className="atlas-topbar-title">
        <strong>{sectionLabel}</strong>
        <span className="muted"> · {me.organisation.name}</span>
      </div>
      <form
        className="atlas-search"
        onSubmit={(e) => {
          e.preventDefault();
          if (searchQ.trim()) {
            navigate(`/learners?q=${encodeURIComponent(searchQ.trim())}`);
          }
        }}
      >
        <input
          type="search"
          placeholder="Search learners…"
          aria-label="Search learners"
          value={searchQ}
          onChange={(e) => setSearchQ(e.target.value)}
          className="atlas-search-input"
        />
      </form>
      <div className="atlas-actions">
        <button
          type="button"
          className="atlas-icon-btn"
          aria-label="Notifications"
          title="Notifications"
          onClick={() => navigate('/notifications')}
        >
          {Icon.bell}
          <span className="dot" />
        </button>
        <button
          type="button"
          className="atlas-icon-btn"
          aria-label="Help"
          title="Help"
        >
          {Icon.help}
        </button>
        <button
          type="button"
          className="atlas-add-btn"
          onClick={() => navigate('/learners/new')}
        >
          <span className="plus">+</span> Add learner
        </button>
      </div>
    </div>
  );
}

function topbarLabel(pathname: string): string {
  if (pathname.startsWith('/learners')) return 'Learners';
  if (pathname.startsWith('/programmes/cohorts')) return 'Cohorts';
  if (pathname.startsWith('/programmes/courses')) return 'Courses';
  if (pathname.startsWith('/programmes/terms')) return 'Terms';
  if (pathname.startsWith('/programmes/rooms')) return 'Rooms';
  if (pathname.startsWith('/programmes/groups')) return 'Groups';
  if (pathname.startsWith('/dashboard')) return 'Dashboard';
  if (pathname.startsWith('/crm/deals')) return 'Deal';
  if (pathname.startsWith('/crm/pipeline')) return 'Pipeline';
  if (pathname.startsWith('/crm/activities')) return 'Activities';
  if (pathname.startsWith('/finance/invoices')) return 'Invoices';
  if (pathname.startsWith('/mis/attendance')) return 'Attendance';
  if (pathname.startsWith('/mis/timetable')) return 'Timetable';
  if (pathname.startsWith('/mis/send')) return 'SEND';
  if (pathname.startsWith('/mis/safeguarding')) return 'Safeguarding';
  if (pathname.startsWith('/mis/documents')) return 'Documents';
  if (pathname.startsWith('/mis/assessments')) return 'Assessments';
  if (pathname.startsWith('/people/staff')) return 'Staff';
  if (pathname.startsWith('/notifications')) return 'Notifications';
  if (pathname.startsWith('/settings')) return 'Settings';
  if (pathname.startsWith('/system/audit-log')) return 'Audit Log';
  if (pathname.startsWith('/system/export')) return 'Data Export';
  if (pathname.startsWith('/system/bulk')) return 'Bulk Operations';
  if (pathname.startsWith('/mis/exams')) return 'Exams';
  if (pathname.startsWith('/mis/progress')) return 'Progress';
  if (pathname.startsWith('/mis/calendar')) return 'Calendar';
  if (pathname.startsWith('/finance/funding')) return 'Funding';
  if (pathname.startsWith('/people/employers')) return 'Employers';
  if (pathname.startsWith('/programmes/room-booking')) return 'Room Booking';
  if (pathname.startsWith('/system/sites')) return 'Sites';
  if (pathname.startsWith('/system/users')) return 'Users';
  if (pathname.startsWith('/system/search')) return 'Search';
  if (pathname.startsWith('/system/year-rollover')) return 'Year Rollover';
  if (pathname.startsWith('/system/email')) return 'Email Integration';
  if (pathname.startsWith('/system/sms')) return 'SMS Integration';
  if (pathname.startsWith('/system/parent-portal')) return 'Parent Portal';
  if (pathname.startsWith('/system/learner-portal')) return 'Learner Portal';
  if (pathname.startsWith('/reports/attendance')) return 'Attendance Report';
  if (pathname.startsWith('/reports/achievement')) return 'Achievement Report';
  if (pathname.startsWith('/reports/kpi')) return 'KPI Dashboard';
  if (pathname.startsWith('/reports/financial')) return 'Financial Reports';
  if (pathname.startsWith('/reports/dbs')) return 'DBS Checks';
  if (pathname.startsWith('/reports/ilr')) return 'ILR Export';
  if (pathname.startsWith('/reports/census')) return 'Census Return';
  if (pathname.startsWith('/reports/custom')) return 'Custom Reports';
  if (pathname.startsWith('/reports')) return 'Reports';
  if (pathname.startsWith('/system/portal')) return 'Portal Management';
  if (pathname.startsWith('/system/api-keys')) return 'API Keys';
  if (pathname.startsWith('/system/health')) return 'System Health';
  if (pathname.startsWith('/system/backups')) return 'Backups';
  if (pathname.startsWith('/system/rate-limits')) return 'Rate Limits';
  if (pathname.startsWith('/finance/payments')) return 'Payments';
  if (pathname.startsWith('/mis/apprenticeships')) return 'Apprenticeships';
  if (pathname.startsWith('/mis/qualifications')) return 'Qualifications';
  if (pathname.startsWith('/mis/analytics')) return 'Predictive Analytics';
  if (pathname.startsWith('/mis/multi-year')) return 'Multi-Year Programmes';
  if (pathname.startsWith('/help')) return 'Knowledge Base';
  return 'Workspace';
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '·';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// ---------------------------------------------------------------------------
// Inline SVG icon set — kept tiny so we don't pull in a library for the M2.5
// visual pass. Stroke colour inherits from currentColor so the active /
// hover states recolour automatically.
// ---------------------------------------------------------------------------
const Icon = {
  users: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  upload: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  ),
  grid: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" />
      <rect x="14" y="3" width="7" height="7" />
      <rect x="14" y="14" width="7" height="7" />
      <rect x="3" y="14" width="7" height="7" />
    </svg>
  ),
  book: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
    </svg>
  ),
  calendar: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  ),
  pin: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  ),
  bell: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  ),
  shield: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  ),
  help: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  ),
};
